'use client';

import createGlobe from 'cobe';
import { useEffect, useRef, useState } from 'react';

// Land-based coordinates for marker placement (major cities/regions)
const LAND_COORDINATES = [
  // North America
  { lat: 40.7128, lng: -74.0060 }, // New York
  { lat: 34.0522, lng: -118.2437 }, // Los Angeles
  { lat: 41.8781, lng: -87.6298 }, // Chicago
  { lat: 29.7604, lng: -95.3698 }, // Houston
  { lat: 45.5017, lng: -73.5673 }, // Montreal

  // Europe
  { lat: 51.5074, lng: -0.1278 }, // London
  { lat: 48.8566, lng: 2.3522 }, // Paris
  { lat: 52.5200, lng: 13.4050 }, // Berlin
  { lat: 41.9028, lng: 12.4964 }, // Rome
  { lat: 40.4168, lng: -3.7038 }, // Madrid
  { lat: 59.3293, lng: 18.0686 }, // Stockholm

  // Asia
  { lat: 35.6762, lng: 139.6503 }, // Tokyo
  { lat: 39.9042, lng: 116.4074 }, // Beijing
  { lat: 31.2304, lng: 121.4737 }, // Shanghai
  { lat: 28.6139, lng: 77.2090 }, // Delhi
  { lat: 19.0760, lng: 72.8777 }, // Mumbai
  { lat: 1.3521, lng: 103.8198 }, // Singapore

  // Australia/Oceania
  { lat: -33.8688, lng: 151.2093 }, // Sydney
  { lat: -37.8136, lng: 144.9631 }, // Melbourne

  // South America
  { lat: -23.5505, lng: -46.6333 }, // São Paulo
  { lat: -34.6037, lng: -58.3816 }, // Buenos Aires
  { lat: -12.0464, lng: -77.0428 }, // Lima

  // Africa
  { lat: -26.2041, lng: 28.0473 }, // Johannesburg
  { lat: 30.0444, lng: 31.2357 }, // Cairo
  { lat: -1.2921, lng: 36.8219 }, // Nairobi
  { lat: 6.5244, lng: 3.3792 }, // Lagos
];

interface Marker {
  location: [number, number];
  size: number;
  baseSize?: number;
  opacity?: number;
}

interface GlobeProps {
  className?: string;
  markers?: Marker[];
  autoRotate?: boolean;
  showMarkers?: boolean;
  markerCount?: number;
}

export default function Globe({
  className = "",
  markers,
  autoRotate = true,
  showMarkers = true,
  markerCount = 8
}: GlobeProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const globeRef = useRef<any>(null);
  const [randomMarkers, setRandomMarkers] = useState<Marker[]>([]);

  // Generate random markers from land coordinates
  useEffect(() => {
    if (!showMarkers) return;

    const generateRandomMarkers = () => {
      const shuffled = [...LAND_COORDINATES].sort(() => 0.5 - Math.random());
      const selected = shuffled.slice(0, markerCount);

      return selected.map(coord => {
        const baseSize = 0.06 + Math.random() * 0.03; // Larger base size
        return {
          location: [coord.lat, coord.lng] as [number, number],
          size: baseSize,
          baseSize,
          opacity: 1,
        };
      });
    };

    setRandomMarkers(generateRandomMarkers());

    // Update markers every 5 seconds with individual marker fades
    const interval = setInterval(() => {
      // First fade out existing markers
      setRandomMarkers(prev =>
        prev.map(marker => ({
          ...marker,
          size: marker.baseSize! * 0.1, // Shrink to simulate fade out
        }))
      );

      // After fade out, update to new locations
      setTimeout(() => {
        const newMarkers = generateRandomMarkers();
        // Start new markers small
        setRandomMarkers(newMarkers.map(marker => ({
          ...marker,
          size: marker.baseSize! * 0.1,
        })));

        // Fade in new markers
        setTimeout(() => {
          setRandomMarkers(prev =>
            prev.map(marker => ({
              ...marker,
              size: marker.baseSize!,
            }))
          );
        }, 200);
      }, 700);
    }, 5000);

    return () => clearInterval(interval);
  }, [showMarkers, markerCount]);


  useEffect(() => {
    if (!canvasRef.current) return;

    const globe = createGlobe(canvasRef.current, {
      devicePixelRatio: 2,
      width: 400 * 2,
      height: 400 * 2,
      phi: 0,
      theta: 0.15,
      dark: 0,
      diffuse: 1.2,
      mapSamples: 16000,
      mapBrightness: 3,
      baseColor: [0.4, 0.7, 1], // Light blue for ocean
      markerColor: [1, 1, 1], // White for flashing circles
      glowColor: [0.6, 0.8, 1], // Light blue glow
      markers: markers || randomMarkers,
      onRender: (state) => {
        if (autoRotate) {
          state.phi = performance.now() / 10000; // Slow rotation
        }
      },
    });

    globeRef.current = globe;

    return () => {
      globe.destroy();
    };
  }, [markers, randomMarkers, autoRotate]);

  return (
    <div className={`flex items-center justify-center ${className}`}>
      <canvas
        ref={canvasRef}
        style={{
          width: 400,
          height: 400,
          maxWidth: '100%',
          aspectRatio: 1,
        }}
      />
    </div>
  );
}

export { Globe };